import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, Check, Image, Download, Share2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LogoGenerator({ studentAnswers, onLogoGenerated }) {
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState(null);
  const [error, setError] = useState("");
  const [remainingGenerations, setRemainingGenerations] = useState(3);
  const [logoHistory, setLogoHistory] = useState([]);

  useEffect(() => {
    loadCurrentLogo();
    loadLogoHistory();
  }, []);

  const loadCurrentLogo = async () => {
    try {
      const response = await axios.get(`${API}/logo/me`);
      if (response.data.has_logo) {
        setLogo(response.data.logo);
        if (onLogoGenerated) onLogoGenerated(response.data.logo);
      }
      setRemainingGenerations(response.data.remaining_regenerations);
    } catch (err) {
      console.error("Error loading logo:", err);
    }
  };

  const loadLogoHistory = async () => {
    try {
      const response = await axios.get(`${API}/logo/history/me`);
      setLogoHistory(response.data.logos || []);
    } catch (err) {
      console.error("Error loading logo history:", err);
    }
  };

  const extractLogoDescription = () => {
    // Try to find the logo description from answers (question 4)
    const answer4 = studentAnswers?.[4] || studentAnswers?.["4"] || "";
    return answer4;
  };

  const extractBusinessName = () => {
    // Try to find the business name from answers (question 1)
    const answer1 = studentAnswers?.[1] || studentAnswers?.["1"] || "";
    return answer1;
  };

  const handleGenerate = async () => {
    const description = extractLogoDescription();
    const businessName = extractBusinessName();

    if (!description || description.trim().length < 10) {
      setError("Please describe your logo idea in Question 4 first (at least 10 characters)");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/logo/generate`, {
        description: description,
        business_name: businessName || "Lemonade Stand"
      });

      setLogo(response.data);
      setRemainingGenerations(response.data.remaining_regenerations);
      if (onLogoGenerated) onLogoGenerated(response.data);
    } catch (err) {
      console.error("Error generating logo:", err);
      setError(err.response?.data?.detail || "Failed to generate logo. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLogo = async (logoId) => {
    try {
      await axios.put(`${API}/logo/${logoId}/select`);
      await loadCurrentLogo();
      await loadLogoHistory();
    } catch (err) {
      console.error("Error selecting logo:", err);
    }
  };

  const handleDownloadLogo = () => {
    if (!logo) return;
    
    const link = document.createElement("a");
    link.download = "my_lemonade_stand_logo.png";
    link.href = `data:image/png;base64,${logo.image_base64}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShareLogo = async () => {
    if (!logo) return;
    
    try {
      // Convert base64 to blob for sharing
      const response = await fetch(`data:image/png;base64,${logo.image_base64}`);
      const blob = await response.blob();
      const file = new File([blob], "my_lemonade_stand_logo.png", { type: "image/png" });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "My Lemonade Stand Logo",
          text: "Check out the logo I created for my lemonade stand business!",
          files: [file]
        });
      } else {
        // Fallback: just download
        handleDownloadLogo();
      }
    } catch (err) {
      console.error("Error sharing logo:", err);
      // Fallback to download
      handleDownloadLogo();
    }
  };

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border-2 border-yellow-200 mt-6" data-testid="logo-generator">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center">
          <Image className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800">Generate Your Logo!</h3>
          <p className="text-sm text-gray-600">
            Turn your logo idea into a real image with AI
          </p>
        </div>
      </div>

      {/* Current Logo Display */}
      {logo && (
        <div className="mb-6">
          <div className="bg-white rounded-xl p-4 shadow-md">
            <div className="flex items-start gap-4">
              <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-yellow-300 flex-shrink-0">
                <img
                  src={`data:image/png;base64,${logo.image_base64}`}
                  alt="Your Business Logo"
                  className="w-full h-full object-contain"
                  data-testid="generated-logo"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="font-semibold text-green-700">Your Selected Logo (Version {logo.version})</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Based on: &quot;{logo.description?.substring(0, 100)}...&quot;
                </p>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleDownloadLogo}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors font-medium"
                    data-testid="download-logo-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    onClick={handleShareLogo}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors font-medium"
                    data-testid="share-logo-btn"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Logo Versions - Always show when multiple versions exist */}
      {logoHistory.length > 1 && (
        <div className="mb-6 bg-white rounded-xl p-4 border-2 border-purple-200">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">🎨</span>
            <h4 className="font-bold text-gray-800">Choose Your Favorite Logo</h4>
            <span className="text-sm text-gray-500">({logoHistory.length} versions)</span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Click on any logo to select it. Your selected logo will appear on your Business Plan and Certificate!
          </p>
          <div className="grid grid-cols-3 gap-4">
            {logoHistory.map((historyLogo) => (
              <div
                key={historyLogo.id}
                className={`relative cursor-pointer rounded-lg overflow-hidden border-3 transition-all hover:scale-105 ${
                  historyLogo.is_selected 
                    ? "border-yellow-400 ring-2 ring-yellow-200 shadow-lg" 
                    : "border-gray-200 hover:border-purple-300"
                }`}
                onClick={() => handleSelectLogo(historyLogo.id)}
                data-testid={`logo-version-${historyLogo.version}`}
              >
                <img
                  src={`data:image/png;base64,${historyLogo.image_base64}`}
                  alt={`Logo Version ${historyLogo.version}`}
                  className="w-full h-28 object-contain bg-gray-50 p-2"
                />
                <div className={`absolute bottom-0 left-0 right-0 text-xs p-2 text-center font-medium ${
                  historyLogo.is_selected 
                    ? "bg-yellow-400 text-yellow-900" 
                    : "bg-gray-100 text-gray-700"
                }`}>
                  {historyLogo.is_selected ? "✓ Selected" : `Version ${historyLogo.version}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="logo-error">
          {error}
        </div>
      )}

      {/* Generate/Regenerate Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleGenerate}
          disabled={loading || remainingGenerations <= 0}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
            loading || remainingGenerations <= 0
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-yellow-400 to-orange-400 text-white hover:from-yellow-500 hover:to-orange-500 shadow-lg hover:shadow-xl"
          }`}
          data-testid="generate-logo-btn"
        >
          {loading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Generating... (may take up to 1 min)
            </>
          ) : logo ? (
            <>
              <Sparkles className="w-5 h-5" />
              Regenerate Logo ({remainingGenerations} left)
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Generate My Logo!
            </>
          )}
        </button>

        {remainingGenerations <= 0 && (
          <span className="text-sm text-gray-500">
            Maximum generations reached
          </span>
        )}
      </div>

      {/* Help Text */}
      <p className="text-xs text-gray-500 mt-4">
        💡 Tip: Your logo will appear on your Business Plan and Certificate! 
        Download it to use on flyers, posters, or social media for your lemonade stand.
      </p>
    </div>
  );
}

export default LogoGenerator;
